/**
 * Help Tooltip Component
 * Displays contextual help information on hover or click
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const HelpTooltip = ({ content, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, alignment: 'center' });
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  const handleShow = () => {
    setIsVisible(true);
  };

  const calculatePosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const tooltipWidth = 288; // w-72 = 18rem = 288px
    const tooltipHeight = tooltipRef.current?.offsetHeight || 100;
    const margin = 10;
    const arrowHeight = 8;

    // Calculate vertical position (above the button)
    let top = buttonRect.top - tooltipHeight - arrowHeight;

    // Calculate horizontal position
    let left = buttonRect.left + (buttonRect.width / 2) - (tooltipWidth / 2);
    let alignment = 'center';
    let arrowOffset = tooltipWidth / 2;

    // Check if it would overflow left
    if (left < margin) {
      left = margin;
      alignment = 'left';
      arrowOffset = buttonRect.left + (buttonRect.width / 2) - margin;
    }
    // Check if it would overflow right
    else if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
      alignment = 'right';
      arrowOffset = buttonRect.left + (buttonRect.width / 2) - left;
    }

    // If tooltip would overflow top, show it below instead
    if (top < margin) {
      top = buttonRect.bottom + arrowHeight;
    }

    setPosition({ top, left, alignment, arrowOffset });
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      // Recalculate on scroll or resize
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible]);

  const getArrowStyle = () => {
    return { left: `${position.arrowOffset}px` };
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        className={`text-gray-400 hover:text-blue-600 transition-colors ${className}`}
        onMouseEnter={handleShow}
        onMouseLeave={() => setIsVisible(false)}
        onClick={(e) => {
          e.preventDefault();
          setIsVisible(!isVisible);
        }}
      >
        <HelpCircle size={16} />
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
        >
          <div className="relative">
            {content}
            {/* Arrow pointing down */}
            <div
              className="absolute -bottom-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900 transform -translate-x-1/2"
              style={getArrowStyle()}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

HelpTooltip.propTypes = {
  content: PropTypes.node.isRequired,
  className: PropTypes.string
};

export default HelpTooltip;
